import {
    ChangeDetectorRef,
    Directive,
    ElementRef,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewRef
} from '@angular/core';
import { NCatalogUi } from '@df/catalog-ui/df-catalog-ui.config';
import { Filter } from '@df/catalog-ui/listing/filter/filter';
import { ProductListing } from '@df/catalog-ui/listing/product-listing';
import { Category, ICategoryDataV2 } from '@df/catalog/category/category';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { isInViewport } from '@df/dom/fn/is-in-viewport';
import { TimeoutService } from '@df/ng/timeout.service';
import { SearchResult } from '@df/search/search-result';
import { Subscription } from 'rxjs';

@Directive()
export class ProductListingComponentDef<T extends Product<IProductDataV2> = Product<IProductDataV2>>
    implements OnDestroy, OnChanges, OnInit
{
    /**
     * Usage context - just as helper
     */
    @Input()
    context?: 'search' | string;

    @Input()
    category?: Category<ICategoryDataV2>;

    @Input()
    searchResult?: SearchResult;

    /**
     * Use to optimize CLS
     */
    @Input()
    delay?: number;

    /**
     * Listing model subscription
     */
    listingSubscription?: Subscription;
    /**
     * Listing model
     */
    _listing?: ProductListing<T>;

    filtersWithSelection: Filter<T>[] = [];

    protected moduleConfig = inject(NCatalogUi.OPTIONS);
    protected subscriptions = new Subscription();

    private initiated = false;
    private listingModelsSubscription?: Subscription;

    constructor(
        protected changeDetectorRef: ChangeDetectorRef,
        protected elementRef: ElementRef,
        protected timeoutService: TimeoutService,
        readonly client: Client,
        readonly app: App
    ) {
        this.changeDetectorRef.detach();
    }

    @Input()
    get listing(): ProductListing<T> | undefined {
        return this._listing;
    }
    set listing(listing: ProductListing<T>) {
        this.unsubscribe();
        this._listing = listing;

        if (listing) {
            this.listingSubscription = listing.subscribe(() => this.onListingUpdate());
            // after few seconds start watching for models changes to jump to results if needed, not to do it initially
            this.timeoutService.setTimeout(() => {
                if (this.listingSubscription) {
                    this.listingModelsSubscription = listing.finalModelsChange.subscribe(() => this.onModelsUpdate());
                }
            }, 5000);
        }
        this.onListingUpdate();
        this.onListingChange();
    }

    ngOnInit() {
        this.subscriptions.add(this.client.obs.breakpoint.subscribe(() => this.detectChanges()));

        const cb = () => {
            this.initiated = true;
            this.detectChanges();
        };

        if (this.delay) {
            this.timeoutService.setTimeout(cb, this.delay);
        } else {
            cb();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.detectChanges();
    }

    /**
     * Cleanup
     */
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
        this.unsubscribe();
    }

    resetFilters(): void {
        this.listing?.resetFilters();
    }

    protected onListingChange() {}

    /**
     * Callback after models are updated
     */
    protected onModelsUpdate() {
        this.returnToListingTop();
    }

    /**
     * Goes back to top of the listing
     */
    protected returnToListingTop() {
        if (this.app.isPlatformBrowser) {
            const min = this.moduleConfig.listing.resetTop;
            if (min !== undefined) {
                // do nothing if top of the listing is in viewport
                if (isInViewport(this.elementRef.nativeElement, 100, 'top')) {
                    return;
                }
                const rect = (this.elementRef.nativeElement as HTMLElement).getBoundingClientRect();
                const minVal = (Array.isArray(min) ? min[this.client.sizeId] : min) || 0;
                const scrollTop = this.client.getRootElement().scrollTop;

                // typical case - we are below then just scroll to it
                if (rect.top < minVal) {
                    const topOnPage = scrollTop + rect.top;
                    this.client.getRootElement().scrollTop = minVal + topOnPage;
                }
                // just to cover a case whern listing is not in view at all then lets jump to it
                else {
                    this.elementRef.nativeElement.scrollIntoView(true);
                }
            }
        }
    }

    /**
     * Unsubscribes listing changes if subscription exists
     */
    protected unsubscribe() {
        this.listingSubscription?.unsubscribe();
        this.listingModelsSubscription?.unsubscribe();
        this.listingSubscription = undefined;
    }

    protected onListingUpdate(): void {
        this.filtersWithSelection = this.listing?.getFiltersWithSelection() || [];
        this.detectChanges();
    }

    protected detectChanges(): void {
        if (this.initiated) {
            this.timeoutService.async(() => {
                if (this.changeDetectorRef && !(<ViewRef>this.changeDetectorRef).destroyed) {
                    this.changeDetectorRef.detectChanges();
                }
            });
        }
    }
}
